import React, { useState } from 'react';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import EmptyState from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/formatters';

const UserTable = ({ users, loading, onToggleStatus, onDeleteUser, onEditUser, onBulkAction, filtered = false, onClearFilters }) => {
  // Table-local sorting: this component owns the rows it renders.
  const { sort, toggle, sortRows } = useSort('createdAt', 'desc');
  const f = useTableFilters(users, React.useMemo(() => ({
    institution: { label: 'Institution', accessor: (u) => u?.institution || '', emptyLabel: 'No institution' },
    isActive:    { label: 'Status', accessor: (u) => (u?.isActive ? 'active' : 'inactive'),
      order: ['active','inactive'], format: (v) => (v === 'active' ? 'Active' : 'Inactive') },
  }), []));
  const SORT_ACCESSORS = {
    name: (r) => `${r?.lastName || ''} ${r?.firstName || ''}`.trim() || r?.fullName || r?.email || '',
    isActive: (r) => (r?.isActive ? 1 : 0),
  };
  const sortedRows = sortRows(f.filterRows(users), SORT_ACCESSORS);
  // Paginate after sorting, so page 1 is the top of the sorted list
  // rather than the top of an arbitrary slice.
  const { pageRows, ...pager } = usePagination(sortedRows, 25);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users?.map(u => u?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev?.filter(id => id !== userId));
    }
  };

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedUsers);
    setSelectedUsers([]);
    setShowBulkActions(false);
  };

  const formatDateSafe = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString) || 'Invalid date';
  };

  if (loading) {
    return (
      // Mirrors the table it's about to become, so nothing jumps on arrival.
      <TableSkeleton rows={8} columns={6} />
    );
  }

  if (!users || users?.length === 0) {
    return (
      // An empty list because you filtered it is a different situation from an
      // empty list because there is nothing to show. Saying "No users found" for
      // both is how an admin concludes the accounts are gone.
      <EmptyState
        filtered={filtered}
        onClear={onClearFilters}
        icon="Users"
        title={filtered ? 'No users match these filters' : 'No users yet'}
        body={
          filtered
            ? 'Try a broader search, or clear the filters to see every account.'
            : 'Registered accounts will appear here as people sign up.'
        }
      />
    );
  }

  return (
    <div>
      {/* Bulk Actions Bar */}
      {selectedUsers?.length > 0 && (
        <div className="p-4 bg-primary/10 border-b-2 border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="CheckSquare" size={20} className="text-primary" />
            <span className="text-foreground font-medium">
              {selectedUsers?.length} user{selectedUsers?.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction('activate')}
              iconName="UserCheck"
              iconPosition="left"
            >
              Activate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction('deactivate')}
              iconName="UserX"
              iconPosition="left"
            >
              Deactivate
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => handleBulkAction('delete')}
              iconName="Trash2"
              iconPosition="left"
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedUsers([])}
              iconName="X"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table-clear">
          <thead>
            <tr>
              <th className="!w-10">
                <input
                  type="checkbox"
                  checked={selectedUsers?.length === users?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="w-4 h-4 border-2 border-input"
                />
              </th>
              <SortableTh field="name" sort={sort} onSort={toggle}>User</SortableTh>
              <SortableTh field="email" sort={sort} onSort={toggle}>Email</SortableTh>
              <SortableTh field="institution" sort={sort} onSort={toggle} filter={f.headerProps('institution')}>Institution</SortableTh>
              <th>Roles</th>
              <SortableTh field="isActive" sort={sort} onSort={toggle} filter={f.headerProps('isActive')}>Status</SortableTh>
              <SortableTh field="createdAt" sort={sort} onSort={toggle} align="end">Joined</SortableTh>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows?.map((user) => (
              <tr key={user?.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => handleSelectUser(user?.id, e?.target?.checked)}
                    className="w-4 h-4 border-2 border-input"
                  />
                </td>
                <td>
                  <div className="font-medium text-foreground">
                    {user?.firstName} {user?.lastName}
                  </div>
                </td>
                <td>
                  <div className="text-muted-foreground">{user?.email}</div>
                </td>
                <td>
                  <div className="text-muted-foreground">{user?.institution || 'N/A'}</div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {user?.roles?.length > 0 ? (
                      user?.roles?.map((role, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 text-xs font-medium ${
                            role === 'admin' ?'bg-error/10 text-error'
                              : role === 'chair' ?'bg-primary/10 text-primary'
                              : role === 'reviewer' ?'bg-secondary/10 text-secondary' :'bg-muted text-muted-foreground'
                          }`}
                        >
                          {role}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">No roles</span>
                    )}
                  </div>
                </td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs font-medium ${
                      user?.isActive
                        ? 'bg-success/10 text-success' :'bg-error/10 text-error'
                    }`}
                  >
                    {user?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="text-muted-foreground">
                    {formatDate(user?.createdAt)}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEditUser(user)}
                      className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit user"
                    >
                      <Icon name="Edit" size={18} className="text-primary" />
                    </button>
                    <button
                      onClick={() => onToggleStatus(user?.id, user?.isActive)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title={user?.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <Icon
                        name={user?.isActive ? 'UserX' : 'UserCheck'}
                        size={18}
                        className="text-muted-foreground"
                      />
                    </button>
                    <button
                      onClick={() => onDeleteUser(user?.id)}
                      className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                      title="Delete user"
                    >
                      <Icon name="Trash2" size={18} className="text-error" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pager} />
    </div>
  );
};

export default UserTable;